import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AnonymousPortalSettings } from '../models/anonymous-portal-settings.model';
import { SpanishAcknowledgmentContent } from './spanish-acknowledgment';
import { GlobalConfigService } from './global-config.service';

const CACHE_PREFIX = 'anonymous-portal-settings:';

// Single source of truth for the per-company anonymous portal settings bag (the language toggle
// today, plus the footer disclosure HTML; a localized/Spanish disclosure would slot in here too).
// Fetched once from the anonymous endpoint (safe on public and authenticated pages) — keyed by
// companyGuid when one is on the URL, otherwise resolved by the page's Referer header. Seeded from
// localStorage to avoid a flash. Consumers gate visibility on these observables.
@Injectable({ providedIn: 'root' })
export class AnonymousPortalSettingsService {

  private readonly _enabled = new BehaviorSubject<boolean>(false);
  private readonly _notice = new BehaviorSubject<string>('');
  private readonly _footerDisclosureHtml = new BehaviorSubject<string>('');
  private readonly _spanishAcknowledgment = new BehaviorSubject<SpanishAcknowledgmentContent | null>(null);

  readonly languageToggleEnabled$ = this._enabled.asObservable();
  readonly languageToggleNotice$ = this._notice.asObservable();
  readonly footerDisclosureHtml$ = this._footerDisclosureHtml.asObservable();
  readonly spanishAcknowledgment$ = this._spanishAcknowledgment.asObservable();

  private _loadedFor: string | null = null;

  constructor(private readonly _globalConfig: GlobalConfigService) {}

  ensureLoaded(companyGuid: string | undefined | null): void {
    if (companyGuid) {
      this.load(companyGuid, () => this._globalConfig.getAnonymousPortalSettings(companyGuid));
      return;
    }

    // No companyGuid on the URL: let the server resolve the company from the page's Referer header.
    // Key the cache by host so different white-label domains stay isolated.
    const host = (typeof window !== 'undefined' && window.location?.host) || '';
    this.load('referrer:' + host, () => this._globalConfig.getAnonymousPortalSettingsByReferrer());
  }

  private load(key: string, fetch: () => Observable<AnonymousPortalSettings>): void {
    if (key === this._loadedFor) return;
    this._loadedFor = key;

    this.apply(this.readCache(key));

    fetch().subscribe({
      next: settings => { this.apply(settings); this.writeCache(key, settings); },
      error: () => { this._loadedFor = null; } // fail closed (hidden); allow a later retry
    });
  }

  private apply(settings: AnonymousPortalSettings | null): void {
    if (!settings) return;
    this._enabled.next(!!settings.enableSpanishToggle);
    this._notice.next(settings.spanishToggleNotice ?? '');
    this._footerDisclosureHtml.next(settings.footerDisclosureHtml ?? '');
    this._spanishAcknowledgment.next(settings.spanishAcknowledgment ?? null);
  }

  private readCache(key: string): AnonymousPortalSettings | null {
    try { return JSON.parse(localStorage.getItem(CACHE_PREFIX + key) ?? 'null'); }
    catch { return null; }
  }

  private writeCache(key: string, settings: AnonymousPortalSettings): void {
    try { localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(settings)); }
    catch { /* ignore */ }
  }
}
